/**
 * Account Intent Config Hooks
 * Per-intent automation toggle for the caller's account.
 */

import { useAtom } from 'jotai';
import { atomWithQuery, atomWithMutation } from 'jotai-tanstack-query';
import { accountIntentConfigService } from '@/services';
import { invalidateQueries } from '@/lib/query-client';
import { buScopeKey, buScopeParams } from '@/lib/business-unit-scope';
import type { IntentChecks } from '@/services/accountIntentConfig.service';

// The brand is in every settings key as well as in the request. Two brands must never
// read each other's cached settings, and switching brands no longer clears the cache —
// a key without the brand would serve the previous brand's values.
const enabledIntentConfigsQueryAtom = atomWithQuery((get) => ({
  queryKey: ['account-intent-configs', 'enabled', buScopeKey(get)],
  queryFn: () => accountIntentConfigService.getEnabledConfigs(buScopeParams(get))
}));

const upsertIntentConfigMutationAtom = atomWithMutation((get) => ({
  mutationKey: ['account-intent-configs', 'upsert'],
  mutationFn: ({ intentId, isAutomationEnabled }: { intentId: string; isAutomationEnabled: boolean }) =>
    accountIntentConfigService.upsertConfig(intentId, isAutomationEnabled, buScopeParams(get)),
  onSuccess: () => {
    invalidateQueries(['account-intent-configs']);
  }
}));

const intentChecksQueryAtom = atomWithQuery((get) => ({
  queryKey: ['account-intent-configs', 'checks', buScopeKey(get)],
  queryFn: () => accountIntentConfigService.getChecks(buScopeParams(get))
}));

const setIntentChecksMutationAtom = atomWithMutation((get) => ({
  mutationKey: ['account-intent-configs', 'checks', 'set'],
  mutationFn: ({ intentId, checks }: { intentId: string; checks: IntentChecks }) =>
    accountIntentConfigService.setChecks(intentId, checks, buScopeParams(get)),
  onSuccess: () => {
    invalidateQueries(['account-intent-configs', 'checks']);
  }
}));

/** The automation checks each intent has for the selected brand. */
export function useIntentChecks() {
  const [query] = useAtom(intentChecksQueryAtom);
  return query;
}

export function useSetIntentChecks() {
  const [mutation] = useAtom(setIntentChecksMutationAtom);
  return mutation;
}

export function useEnabledIntentConfigs() {
  const [query] = useAtom(enabledIntentConfigsQueryAtom);
  return query;
}

export function useUpsertIntentConfig() {
  const [mutation] = useAtom(upsertIntentConfigMutationAtom);
  return mutation;
}

